import { AccountLevelEnum, AccountStatusEnum, SnackBarVariants, UserRole } from '@app/app.enum';
import { history } from '@app/app.routes';
import store from '@app/app.store';
import { withAxiosCancellation } from '@app/components/core/axios-cancel.component';
import withSnackBar from '@app/components/core/message.component';
import CloseAccountComponent from '@app/components/shared/close-account.component';
import CropImage from '@app/components/shared/crop-image.component';
import CustomButton from '@app/components/shared/custom-button.component';
import Icon from '@app/components/shared/icon.component';
import PasswordTextFieldComponent from '@app/components/shared/password-text-field.component';
import * as constants from '@app/constants';
import {
	ChangePasswordNowLink,
	ChangePasswordRegex,
	CompressedImgHeight,
	CompressedImgWidth,
	defaultCountry,
} from '@app/constants';
import * as models from '@app/models/index';
import Injector from '@app/services';
import { AccountService } from '@app/services/account.service';
import { IntlService } from '@app/services/intl.service';
import { MetaDataService } from '@app/services/meta-data.service';
import { SharedService } from '@app/services/shared.service';
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Button,
	CircularProgress,
	Grid,
	Hidden,
	Link,
	TextField,
	Tooltip,
	Typography,
	withStyles,
} from '@material-ui/core';
import Axios, { CancelTokenSource } from 'axios';
import dayjs from 'dayjs';
import { Field, Form, Formik, FormikActions, FormikProps, FormikValues } from 'formik';
import * as React from 'react';
import { FormattedDate, injectIntl } from 'react-intl';
import * as Yup from 'yup';

const intlService: IntlService = Injector.get(IntlService);
const accountService: AccountService = Injector.get(AccountService);
const sharedServive: SharedService = Injector.get(SharedService);
const metaDataService: MetaDataService = Injector.get(MetaDataService);

const HtmlTooltip = withStyles((theme) => ({
	tooltip: {
		backgroundColor: theme.palette.background.default,
		color: theme.palette.text.primary,
		minWidth: 450,
		border: `1px solid ${theme.palette.grey[700]}`,
	},
}))(Tooltip);

const userInfoSchema = Yup.object().shape<any>({
	firstName: Yup.string()
		.required(intlService.GetFormattedMessage('app.profile.user-info-form.first-name-required-validation'))
		.max(100, intlService.GetFormattedMessage('app.profile.user-info-form.first-name-max-length-validation')),
	lastName: Yup.string()
		.required(intlService.GetFormattedMessage('app.profile.user-info-form.last-name-required-validation'))
		.max(100, intlService.GetFormattedMessage('app.profile.user-info-form.last-name-max-length-validation')),
	email: Yup.string()
		.trim()
		.email(intlService.GetFormattedMessage('app.signup.signup-form.email-invalid-validation'))
		.required(intlService.GetFormattedMessage('app.signup.signup-form.email-required-validation')),
});

const changePasswordSchema = Yup.object().shape<models.IChangePassword>({
	oldPassword: Yup.string().required(
		intlService.GetFormattedMessage('app.profile.change-password-form.old-password-required-validation')
	),
	newPassword: Yup.string()
		.required(intlService.GetFormattedMessage('app.profile.change-password-form.new-password-required-validation'))
		.matches(ChangePasswordRegex, intlService.GetFormattedMessage('app.profile.change-password-form.regex-validation')),
	confirmPassword: Yup.string()
		.oneOf(
			[Yup.ref('newPassword')],
			intlService.GetFormattedMessage('app.profile.change-password-form.new-password.confirm.password-validation')
		)
		.required(intlService.GetFormattedMessage('app.profile.change-password-form.confirm-password-required-validation')),
});

class ProfileComponent extends React.Component<models.IProfileProps, models.IProfileState> {
	public fileName: string;
	public formRef = React.createRef<Formik<FormikValues>>();
	private userData: models.IUser;
	private userDetails: models.IUserSignInResponse;
	private userInfoFormFactory: models.IUserBasicInfo;
	private changePasswordFormFactory: models.IChangePassword;

	private dialogCancellationSource: CancelTokenSource;
	private authentication = store.getState().authentication;
	constructor(props: models.IProfileProps) {
		super(props);
		this.state = models.initialProfileState();
		this.userDetails = accountService.getUserData();

		this.userInfoFormFactory = {
			email: '',
			firstName: '',
			lastName: '',
			visibleAccountNumber: '',
			userName: '',
		};
		this.changePasswordFormFactory = {
			oldPassword: '',
			newPassword: '',
			confirmPassword: '',
		};
	}

	public componentDidMount() {
		this.getStatesForCountry();
		this.getUserDetails();
		this.setState({
			isAccountClosed: store.getState().authentication.status === AccountStatusEnum.Closed,
			passwordChangeDueDate: this.userDetails.user?.passwordChangeDueDate,
		});
	}

	public render() {
		const role = this.authentication.user ? this.authentication.user.role : '';

		let resendVerificationLinkComponent = (
			<Link href='#' underline='always' onClick={() => this.onResendVerificationEmail()}>
				{this.props.intl.formatMessage({
					id: 'app.profile.user-info-form.resend-verification-mail-text',
				})}
			</Link>
		);
		if (this.state.hideResendVerificationLink || this.state.isEmailVerified) {
			resendVerificationLinkComponent = null;
		}
		const closeAccountComponent = !this.state.isAccountClosed ? (
			role === UserRole.User ? (
				<Typography
					variant='body2'
					classes={{
						root: 'disabled-link',
					}}
				>
					{intlService.GetFormattedMessage('app.profile.user-info-form.close.account-link')}
				</Typography>
			) : (
				<Link
					href='#'
					underline='always'
					onClick={() => {
						this.openDialog();
					}}
				>
					{intlService.GetFormattedMessage('app.profile.user-info-form.close.account-link')}
				</Link>
			)
		) : null;
		return (
			<div>
				{/* Profile Section*/}
				<Grid container={true} direction='column' spacing={2}>
					<Grid item={true} lg={12}>
						{/* Heading */}
						<Accordion defaultExpanded={history.location.state?.extraParams?.isRoutedFrom !== ChangePasswordNowLink}>
							<AccordionSummary expandIcon={<Icon name='chevron-down' />}>
								<Grid container={true} direction='row' justifyContent='space-evenly'>
									<Grid item={true} xs={12}>
										<Typography variant='h6' color='inherit'>
											{this.props.intl.formatMessage({
												id: 'app.profile-label',
											})}
										</Typography>
									</Grid>
								</Grid>
							</AccordionSummary>
							{/* user Info Form */}
							<AccordionDetails>
								{this.state.dataLoading ? (
									<div className='spinner-for-panels'>
										<CircularProgress size={32} />
									</div>
								) : (
									<Formik
										initialValues={this.userInfoFormFactory}
										onSubmit={(e: models.IUserBasicInfo) => this.updateUserBasicInfo(e)}
										validationSchema={userInfoSchema}
										enableReinitialize={true}
										render={(props: FormikProps<FormikValues>) => (
											<Form noValidate={true} className='width-100'>
												<Grid container={true} direction='row' alignItems='center' spacing={2}>
													{/* First Name */}
													<Grid item={true} xs={12} sm={12} md={12} lg={12} xl={12}>
														<Grid container={true} direction='row' alignItems='center'>
															<Grid item={true} xs={12} md={2} lg={3}>
																<Typography variant='subtitle2' color='inherit' className='required'>
																	{this.props.intl.formatMessage({
																		id: 'app.profile-user-info-form.first-name-label',
																	})}
																</Typography>
															</Grid>
															<Grid item={true} xs={12} md={10} lg={9}>
																<Field
																	name='firstName'
																	margin='normal'
																	disabled={this.userDetails.account.level === AccountLevelEnum.Partner}
																	component={TextField}
																	type='text'
																	fullWidth={true}
																	value={props.values.firstName}
																	required={true}
																	onChange={(e: React.ChangeEvent) => props.handleChange('firstName')(e)}
																	error={
																		!!((props.values.firstName.length || props.submitCount) && !!props.errors.firstName)
																	}
																	helperText={
																		props.values.firstName.length || props.submitCount ? props.errors.firstName : null
																	}
																/>
															</Grid>
														</Grid>
													</Grid>
													{/* LastName */}
													<Grid item={true} xs={12} sm={12} md={12} lg={12} xl={12}>
														<Grid container={true} direction='row' alignItems='center'>
															<Grid item={true} xs={12} md={2} lg={3}>
																<Typography variant='subtitle2' color='inherit' className='required'>
																	{this.props.intl.formatMessage({
																		id: 'app.profile-user-info-form.last-name-label',
																	})}
																</Typography>
															</Grid>
															<Grid item={true} xs={12} md={10} lg={9}>
																<Field
																	name='lastName'
																	margin='normal'
																	disabled={this.userDetails.account.level === AccountLevelEnum.Partner}
																	component={TextField}
																	type='text'
																	fullWidth={true}
																	value={props.values.lastName}
																	required={true}
																	onChange={(e: React.ChangeEvent) => props.handleChange('lastName')(e)}
																	error={
																		!!((props.values.lastName.length || props.submitCount) && !!props.errors.lastName)
																	}
																	helperText={
																		props.values.lastName.length || props.submitCount ? props.errors.lastName : null
																	}
																/>
															</Grid>
														</Grid>
													</Grid>
													{/* Email */}
													<Grid item={true} xs={12} sm={12} md={12} lg={12} xl={12}>
														<Grid container={true} direction='row' alignItems='center'>
															<Grid item={true} xs={12} md={2} lg={3}>
																<Typography variant='subtitle2' color='inherit' className='required'>
																	{this.props.intl.formatMessage({
																		id: 'app.profile.user-info-form.email-label',
																	})}
																</Typography>
															</Grid>
															<Grid item={true} xs={12} md={10} lg={9}>
																<Field
																	margin='normal'
																	required={true}
																	component={TextField}
																	type='text'
																	fullWidth={true}
																	value={props.values.email}
																	onChange={(e: React.ChangeEvent) => {
																		props.handleChange('email')(e);
																	}}
																	error={!!((props.values.email.length || props.submitCount) && !!props.errors.email)}
																	helperText={
																		props.values.email.length || props.submitCount ? props.errors.email : null
																	}
																/>
															</Grid>
															<Grid item={true} xs={12} className='text-end'>
																<span>{this.userInfoFormFactory.email && resendVerificationLinkComponent}</span>
															</Grid>
														</Grid>
													</Grid>
													{/* UserName */}
													<Grid item={true} xs={12} sm={12} md={12} lg={12} xl={12}>
														<Grid container={true} direction='row' alignItems='center'>
															<Grid item={true} xs={12} md={2} lg={3}>
																<Typography variant='subtitle2' color='inherit' className='required'>
																	{this.props.intl.formatMessage({
																		id: 'app.profile.user-info-form.username-label',
																	})}
																</Typography>
															</Grid>
															<Grid item={true} xs={12} md={10} lg={9}>
																<Field
																	margin='normal'
																	component={TextField}
																	type='text'
																	fullWidth={true}
																	value={props.values.userName}
																	disabled={true}
																/>
															</Grid>
														</Grid>
													</Grid>
													{/* VisibleAccountNumber */}
													{role !== '' && !accountService.IsAdmin(role) ? (
														<Grid item={true} xs={12} sm={12} md={12} lg={12} xl={12}>
															<Grid container={true} direction='row' alignItems='center'>
																<Grid item={true} xs={12} md={2} lg={3}>
																	<Typography variant='subtitle2' color='inherit' className='required'>
																		{this.props.intl.formatMessage({
																			id: 'app.profile.user-info-form.visible-account-number-label',
																		})}
																	</Typography>
																</Grid>
																<Grid item={true} xs={12} md={10} lg={9}>
																	<Field
																		margin='normal'
																		component={TextField}
																		type='text'
																		fullWidth={true}
																		value={props.values.visibleAccountNumber}
																		disabled={true}
																	/>
																</Grid>
																<Grid item={true} xs={12} className='text-end'>
																	{this.userDetails.account.level !== AccountLevelEnum.Partner ? (
																		<Grid container={true} direction='row-reverse'>
																			<Grid item={true} xs={2}>
																				<Grid container={true} alignItems='center' direction='row-reverse'>
																					{role === UserRole.User && (
																						<Grid item={true} xs={2}>
																							<HtmlTooltip
																								arrow={true}
																								placement='bottom'
																								title={
																									<Typography variant='body2' color='inherit'>
																										{intlService.GetFormattedMessage(
																											'app.profile.user-close-account-label',
																											{
																												['0']: this.userDetails.account.users.find(
																													(x) => x.role === UserRole.AccountAdmin
																												)?.email,
																											}
																										)}
																									</Typography>
																								}
																							>
																								<Icon name='info-circle' />
																							</HtmlTooltip>
																						</Grid>
																					)}
																					<Grid item={true} xs={10}>
																						<span>{closeAccountComponent}</span>
																					</Grid>
																				</Grid>
																			</Grid>
																		</Grid>
																	) : null}
																</Grid>
															</Grid>
														</Grid>
													) : null}

													{/* save user info button */}
													<Grid
														container={true}
														direction='row'
														alignItems='center'
														justifyContent='center'
														className='mt-2 mb-2'
													>
														{/* Button Component */}
														<CustomButton
															disabled={this.state.userInfoLoading}
															buttonText={this.props.intl.formatMessage({
																id: 'app.profile.user-info-form.save-button-text',
															})}
															loading={this.state.userInfoLoading}
														/>
													</Grid>
												</Grid>
											</Form>
										)}
									/>
								)}
							</AccordionDetails>
						</Accordion>
					</Grid>
				</Grid>
				{/*Company Panel */}
				<Grid container={true} direction='column' spacing={2}>
					<Grid item={true} lg={12}>
						{/* Heading */}
						<Accordion>
							<AccordionSummary expandIcon={<Icon name='chevron-down' />}>
								<Grid container={true} direction='row' justifyContent='space-evenly'>
									<Grid item={true} xs={12}>
										<Typography variant='h6' color='inherit'>
											{this.props.intl.formatMessage({
												id: 'app.account.company-label',
											})}
										</Typography>
									</Grid>
								</Grid>
							</AccordionSummary>
							{/* user Info Form */}
							<AccordionDetails>
								{this.state.dataLoading ? (
									<div className='spinner-for-panels'>
										<CircularProgress size={32} />
									</div>
								) : (
									<Formik
										initialValues={null}
										enableReinitialize={true}
										onSubmit={() => {}}
										render={(props: FormikProps<FormikValues>) => (
											<Form noValidate={true} className='width-100'>
												<Grid container={true} direction='row' alignItems='center' spacing={2}>
													<Grid item={true} xs={12} sm={12} md={12} lg={12} xl={12}>
														{/*company Name */}
														<Grid container={true} direction='row' alignItems='center'>
															<Hidden only={['xs', 'sm']}>
																<Grid item={true} xs={12} md={2} lg={3}>
																	<Typography variant='subtitle2' color='inherit'>
																		{this.props.intl.formatMessage({
																			id: 'app.profile.logo',
																		})}
																	</Typography>
																</Grid>
															</Hidden>
															<Grid item={true} xs={12} md={10} lg={9}>
																<div className='image-profile'>
																	{this.state.onImgUploading ? (
																		<div className='logo-spinner'>
																			<CircularProgress size={24} />
																		</div>
																	) : (
																		<>
																			<div className='image-profile-overlay'>
																				<Button
																					onClick={() => this.showFileDialog()}
																					size='small'
																					color='inherit'
																					variant='outlined'
																					className='edit-icon'
																				>
																					<Grid container={true} direction='row' alignItems='center'>
																						<Grid className='pr-1' item={true}>
																							<Icon name='pencil' titleAccess='Edit' />
																						</Grid>
																						<Grid item={true}>
																							{this.props.intl.formatMessage({
																								id: 'app.app-view.app-card.edit',
																							})}
																						</Grid>
																					</Grid>
																				</Button>
																			</div>
																			<img src={this.state.logo} width={150} height={150} />
																		</>
																	)}
																</div>
															</Grid>

															{/* Crop Image component */}
															<CropImage
																imageSrc={this.state.imageSrc}
																uploadFileCallback={this.fileCallback}
																isOpen={this.state.isOpen}
																onCropCancel={this.closeImagePreview}
															/>

															<Grid item={true} xs={12} md={2} lg={3}>
																<Typography variant='subtitle2' color='inherit' className='required'>
																	{this.props.intl.formatMessage({
																		id: 'app.profile.company-form.company-name-label',
																	})}
																</Typography>
															</Grid>
															<Grid item={true} xs={12} md={10} lg={9}>
																<Field
																	name='company'
																	margin='normal'
																	component={TextField}
																	type='text'
																	value={this.state.company}
																	fullWidth={true}
																	required={true}
																	disabled={true}
																/>
															</Grid>
														</Grid>
													</Grid>
												</Grid>
											</Form>
										)}
									/>
								)}
							</AccordionDetails>
						</Accordion>
					</Grid>
				</Grid>

				{/* Change Password Section*/}
				{this.userDetails.account.level !== AccountLevelEnum.Partner && (
					<Grid container={true} direction='column' spacing={2}>
						<Grid item={true} lg={12}>
							{/* Heading */}
							<Accordion defaultExpanded={history.location.state?.extraParams?.isRoutedFrom === ChangePasswordNowLink}>
								<AccordionSummary expandIcon={<Icon name='chevron-down' />}>
									<Grid container={true} direction='row' justifyContent='space-evenly'>
										<Grid item={true} xs={12}>
											<Typography variant='h6' color='inherit'>
												{this.props.intl.formatMessage({
													id: 'app.profile.change-password-form.change-password-label',
												})}
											</Typography>
										</Grid>
									</Grid>
								</AccordionSummary>
								{/* Change Password Form */}
								<AccordionDetails>
									<Formik
										initialValues={this.changePasswordFormFactory}
										onSubmit={(e: models.IChangePassword, formikActions: FormikActions<models.IChangePassword>) =>
											this.changePassword(e, formikActions)
										}
										validate={(values) => {
											const errors: any = {};
											const isContainUserOrAccountName = SharedService.isPasswordContainsUserOrAccountName(
												values.newPassword,
												this.userDetails
											);
											if (isContainUserOrAccountName) {
												errors.newPassword = this.props.intl.formatMessage({
													id: 'app.profile.change-password-form.user-account-name-validation',
												});
											}
											return errors;
										}}
										validationSchema={changePasswordSchema}
										render={(props: FormikProps<FormikValues>) => (
											<Form noValidate={true} className='width-100'>
												<Grid container={true} direction='row' alignItems='center' spacing={3}>
													<Grid item={true} xs={12} sm={12} md={12} lg={12} xl={12}>
														{/*  PasswordChangeDueDate */}
														<Grid container={true} direction='row' alignItems='center'>
															<Grid item={true} xs={12} md={2} lg={3}>
																<Typography variant='subtitle2' color='inherit'>
																	{this.props.intl.formatMessage({
																		id: 'app.profile.change-password-form.password-chang-due-date-label',
																	})}
																</Typography>
															</Grid>
															<Grid
																item={true}
																xs={12}
																md={10}
																lg={9}
																className={
																	!dayjs().isBefore(this.state.passwordChangeDueDate, 'day') ? 'red-color' : ''
																}
															>
																{this.state.passwordChangeDueDate && (
																	<FormattedDate
																		{...constants.dateFormatOptions}
																		value={this.state.passwordChangeDueDate}
																	/>
																)}
															</Grid>
														</Grid>

														{/* Old password */}
														<Grid container={true} direction='row' alignItems='center'>
															<Grid item={true} xs={12} md={2} lg={3}>
																<Typography variant='subtitle2' color='inherit'>
																	{this.props.intl.formatMessage({
																		id: 'app.profile.change-password-form.old-password-label',
																	})}
																</Typography>
															</Grid>
															<Grid item={true} xs={12} md={10} lg={9}>
																<Field
																	name='oldPassword'
																	margin='normal'
																	component={PasswordTextFieldComponent}
																	type='password'
																	fullWidth={true}
																	value={props.values.oldPassword}
																	required={true}
																	onChange={(e: React.ChangeEvent) => props.handleChange('oldPassword')(e)}
																	error={
																		!!(
																			(props.values.oldPassword.length || props.submitCount) &&
																			!!props.errors.oldPassword
																		)
																	}
																	helperText={
																		props.values.oldPassword.length || props.submitCount
																			? props.errors.oldPassword
																			: null
																	}
																/>
															</Grid>
														</Grid>
														{/* New password */}
														<Grid container={true} direction='row' alignItems='center'>
															<Grid item={true} xs={12} md={2} lg={3}>
																<Typography variant='subtitle2' color='inherit'>
																	{this.props.intl.formatMessage({
																		id: 'app.profile.change-password-form.new-password-label',
																	})}
																</Typography>
															</Grid>
															<Grid item={true} xs={12} md={10} lg={9}>
																<Field
																	name='newPassword'
																	margin='normal'
																	component={PasswordTextFieldComponent}
																	type='password'
																	fullWidth={true}
																	value={props.values.newPassword}
																	required={true}
																	onChange={(e: React.ChangeEvent) => props.handleChange('newPassword')(e)}
																	error={
																		!!(
																			(props.values.newPassword.length || props.submitCount) &&
																			!!props.errors.newPassword
																		)
																	}
																	helperText={
																		props.values.newPassword.length || props.submitCount
																			? props.errors.newPassword
																			: null
																	}
																/>
															</Grid>
														</Grid>

														{/* {Confirm Password} */}
														<Grid container={true} direction='row' alignItems='center'>
															<Grid item={true} xs={12} md={2} lg={3}>
																<Typography variant='subtitle2' color='inherit'>
																	{this.props.intl.formatMessage({
																		id: 'app.profile.change-password-form.confirm-password-label',
																	})}
																</Typography>
															</Grid>
															<Grid item={true} xs={12} md={10} lg={9}>
																<Field
																	name='confirmPassword'
																	margin='normal'
																	component={PasswordTextFieldComponent}
																	type='password'
																	fullWidth={true}
																	value={props.values.confirmPassword}
																	required={true}
																	onChange={(e: React.ChangeEvent) => props.handleChange('confirmPassword')(e)}
																	error={
																		!!(
																			(props.values.confirmPassword.length || props.submitCount) &&
																			!!props.errors.confirmPassword
																		)
																	}
																	helperText={
																		props.values.confirmPassword.length || props.submitCount
																			? props.errors.confirmPassword
																			: null
																	}
																/>
															</Grid>
														</Grid>
													</Grid>
													{/* changePassword button */}
													<Grid
														container={true}
														direction='row'
														alignItems='center'
														justifyContent='center'
														className='mt-2 mb-2'
													>
														{/* Button component */}
														<CustomButton
															disabled={this.state.changePasswordLoading}
															buttonText={this.props.intl.formatMessage({
																id: 'app.profile.change-password-form.change-password-label',
															})}
															loading={this.state.changePasswordLoading}
														/>
													</Grid>
												</Grid>
											</Form>
										)}
									/>
								</AccordionDetails>
							</Accordion>
						</Grid>
					</Grid>
				)}

				{/* Close account component */}
				<CloseAccountComponent
					openDialog={this.state.openDialog}
					onCloseAccount={this.onCloseAccount}
					closeDialog={(dialogPanel: string, isCancel: boolean) => this.closeDialog(dialogPanel, isCancel)}
				/>
			</div>
		);
	}

	// open dialog
	public openDialog = () => {
		this.setState({ openDialog: true });
	};
	// close dialog
	public closeDialog = (dialogPannel: string, isCancel: boolean) => {
		if (!!this.dialogCancellationSource) {
			this.dialogCancellationSource.cancel();
		}
		this.setState({ openDialog: false });
	};
	// update user basic details
	public async updateUserBasicInfo(eventData: models.IUserBasicInfo) {
		try {
			this.setState({ userInfoLoading: true });
			const result = await accountService.updateUserBasicInfo(eventData, this.props.axiosCancellationToken);
			this.userInfoFormFactory = eventData;
			this.userData = {
				...this.userData,
				...result,
			};

			// Update userData's email in local storage
			this.userDetails.user = this.userData;
			accountService.setUserData(this.userDetails);

			this.userInfoFormFactory = {
				email: this.userData.email,
				userName: this.userData.userName,
				firstName: this.userData.firstName ? this.userData.firstName : '',
				lastName: this.userData.lastName ? this.userData.lastName : '',
				visibleAccountNumber: this.userData.account.visibleAccountNumber
					? this.userData.account.visibleAccountNumber
					: '',
			};
			this.setState({
				userInfoLoading: false,
				isEmailVerified: result.isEmailVerified,
				hideResendVerificationLink: result.isEmailVerified,
			});
			this.props.openSnackBar(
				this.props.intl.formatMessage({
					id: 'app.snackbar.profile-updated-successfully',
				}),
				SnackBarVariants.Success
			);
		} catch (error) {
			this.setState({ userInfoLoading: false });
			if (!Axios.isCancel(error)) {
				this.props.openSnackBar(sharedServive.parseError(error.data), SnackBarVariants.Error);
			}
		}
	}

	// change user password
	public async changePassword(eventData: models.IChangePassword, formikActions: FormikActions<models.IChangePassword>) {
		try {
			this.setState({ changePasswordLoading: true });
			const result = await accountService.ChangePassword(eventData, this.props.axiosCancellationToken);
			this.setState({ changePasswordLoading: false });
			if (result) {
				this.props.openSnackBar(
					this.props.intl.formatMessage({
						id: 'app.snackbar.password-changed-successfully',
					}),
					SnackBarVariants.Success
				);
				formikActions.resetForm();
				this.getUserDetails();
			}
		} catch (error) {
			this.setState({ changePasswordLoading: false });
			if (!Axios.isCancel(error)) {
				this.props.openSnackBar(sharedServive.parseError(error.data), SnackBarVariants.Error);
			}
		}
	}

	// resend verification mail
	private async onResendVerificationEmail() {
		try {
			if (!this.state.hideResendVerificationLink) {
				this.setState({
					hideResendVerificationLink: true,
				});
			}
			const result = await accountService.ResendVerificationEmail(this.props.axiosCancellationToken);
			if (result) {
				this.props.openSnackBar(
					this.props.intl.formatMessage({
						id: 'app.profile.user-info-form.resend-verification-mail',
					}),
					SnackBarVariants.Success
				);
			}
		} catch (error) {
			if (!Axios.isCancel(error)) {
				this.props.openSnackBar(sharedServive.parseError(error.data), SnackBarVariants.Error);
			}
		}
	}

	// close account
	private onCloseAccount = async (eventData: models.ICloseAccountRequest) => {
		try {
			this.dialogCancellationSource = Axios.CancelToken.source();
			const result = await accountService.CloseAccount(eventData, this.dialogCancellationSource.token);
			if (result) {
				this.props.openSnackBar(
					this.props.intl.formatMessage({
						id: 'app.snackbar.close-account-successfully',
					}),
					SnackBarVariants.Success
				);
			}
			this.props.onCloseAccount();

			accountService.AccountStatus = AccountStatusEnum.Closed;

			accountService.setUserData(this.userDetails);

			history.push(models.Module.Dashboard.url);
		} catch (error) {
			this.setState({ openDialog: false });
			if (!Axios.isCancel(error)) {
				this.props.openSnackBar(sharedServive.parseError(error.data), SnackBarVariants.Error);
			}
		}
	};

	// get user own record
	private async getUserDetails() {
		try {
			const result = await accountService.getOwnUserRecord(this.props.axiosCancellationToken);
			this.userData = result;
			// Destructuring properties form userData which will be used below
			const { email, firstName, lastName, isEmailVerified, account, userName, passwordChangeDueDate } = this.userData;

			this.userInfoFormFactory = {
				email,
				userName,
				firstName: firstName ? firstName : '',
				lastName: lastName ? lastName : '',
				visibleAccountNumber: account.visibleAccountNumber ? account.visibleAccountNumber : '',
			};

			if (!!account) {
				this.setState({
					logo: account.logo ? account.logo : this.state.logo,
					company: account.name ? account.name : this.state.company,
				});
			}
			this.setState({
				userInfoLoading: false,
				dataLoading: false,
				isEmailVerified,
				passwordChangeDueDate,
			});
		} catch (error) {
			if (!Axios.isCancel(error)) {
				this.props.openSnackBar(sharedServive.parseError(error.data), SnackBarVariants.Error);
			}
		}
	}

	private showFileDialog = () => {
		sharedServive.showFileDialog(false, 'image/*', this.imageFileReader);
	};

	private imageFileReader = async (res: File | FileList, err: any) => {
		if (res) {
			this.fileName = res[0].name;
			const reader = new FileReader();
			reader.addEventListener('load', () => {
				sharedServive.compressImage(
					reader.result.toString(),
					CompressedImgWidth,
					CompressedImgHeight,
					this.compressImageCallBack
				);
			});
			reader.readAsDataURL(res[0]);
		}
	};

	/**
	 * File callback
	 */
	private fileCallback = async (res: File, err?: any) => {
		if (res) {
			this.setState({
				onImgUploading: true,
			});
			const fileUrl: string = await accountService.UploadCompanyLogo(res, this.props.axiosCancellationToken);
			this.setState({
				logo: fileUrl,
				isOpen: false,
				onImgUploading: false,
			});
		}
	};

	private async getStatesForCountry() {
		try {
			const states = await metaDataService.getStates(defaultCountry, this.props.axiosCancellationToken);
			this.setState({ states });
		} catch (error) {
			if (!Axios.isCancel(error)) {
				this.props.openSnackBar(sharedServive.parseError(error.data), SnackBarVariants.Error);
			}
		}
	}

	private closeImagePreview = (value: boolean) => {
		this.setState({ isOpen: value });
	};

	private compressImageCallBack = (url: string) => {
		this.setState({
			imageSrc: url,
			isOpen: true,
		});
	};
}

export default withAxiosCancellation(withSnackBar(injectIntl(ProfileComponent)));



// WEBPACK FOOTER //
// ./src/components/account/profile/profile.component.tsx